import { useCallback, useSyncExternalStore } from 'react';

type QuerySubscription = {
  media: MediaQueryList;
  listeners: Set<() => void>;
  notify: () => void;
};

// Only populated by client subscriptions; release the query after its last user.
const subscriptions = new Map<string, QuerySubscription>();

function subscribe(query: string, listener: () => void) {
  let subscription = subscriptions.get(query);
  if (!subscription) {
    const media = window.matchMedia(query);
    const listeners = new Set<() => void>();
    const notify = () => listeners.forEach((listener) => listener());
    subscription = { media, listeners, notify };
    subscriptions.set(query, subscription);
    media.addEventListener('change', notify);
  }
  subscription.listeners.add(listener);

  return () => {
    subscription.listeners.delete(listener);
    if (subscription.listeners.size === 0) {
      subscription.media.removeEventListener('change', subscription.notify);
      subscriptions.delete(query);
    }
  };
}

const getServerSnapshot = () => false;

/** Share a live query across consumers while keeping server and hydration HTML equal. */
export function useMediaQuery(query: string): boolean {
  const subscribeToQuery = useCallback((listener: () => void) => subscribe(query, listener), [query]);
  const getSnapshot = useCallback(
    () => (subscriptions.get(query)?.media ?? window.matchMedia(query)).matches,
    [query],
  );

  return useSyncExternalStore(subscribeToQuery, getSnapshot, getServerSnapshot);
}
